/** Shared city ground: simplify small DEM bumps before designing roads or pads. */
import type { TerrainMeshData } from './terrainmesh.js';
import { LANDCOVER } from '../sources/shortbread.js';

/** A separable Gaussian in metres, so terrain detail does not change the result. */
function blur(values: Float32Array, side: number, step: number, horizontal: boolean, sigma: number): Float32Array {
  const radius = Math.ceil((sigma * 180 / 70) / step);
  const weights = Array.from({ length: radius * 2 + 1 }, (_, k) => Math.exp(-0.5 * ((k - radius) * step / sigma) ** 2));
  const out = new Float32Array(values.length);
  for (let i = 0; i < side; i++) for (let j = 0; j < side; j++) {
    let sum = 0, weight = 0;
    for (let k = -radius; k <= radius; k++) {
      const row = horizontal ? i : i + k, col = horizontal ? j + k : j;
      if (row < 0 || col < 0 || row >= side || col >= side) continue;
      const w = weights[k + radius];
      sum += values[row * side + col] * w;
      weight += w;
    }
    out[i * side + j] = sum / weight;
  }
  return out;
}

/** How wide the city's ground is smoothed, metres (a Gaussian's sigma): on the coarse elevation models, and on the laser ground model. */
export const CITY_SMOOTH = 70;
export const LASER_CITY_SMOOTH = 15;
/**
 * On the laser model, the most the city's levelling moves the ground,
 * metres: enough to even the small bumps between blocks, never to take away
 * a real bank. Smoothed freely, a 2 m bank up to a footbridge's stairs was
 * smeared into a long low slope, and the stairs stood on the flat.
 */
export const LASER_CITY_CAP = 0.35;

const smoothOn = (mesh: TerrainMeshData, v: Float32Array, sigma = CITY_SMOOTH) =>
  blur(blur(v, mesh.gridRes + 1, mesh.width / mesh.gridRes, true, sigma), mesh.gridRes + 1, mesh.depth / mesh.gridRes, false, sigma);

/**
 * How built-up the land round each grid vertex is, 0 open land to 1 city,
 * eased so a block's outskirts blend into the fields; undefined where the
 * tile has no built-up cover at all. Urban pavement, gardens, driveways and
 * parking count.
 */
export function urbanWeight(mesh: TerrainMeshData, cover: Uint8Array, size: number): Float32Array | undefined {
  const side = mesh.gridRes + 1;
  const mask = new Float32Array(side * side);
  for (let i = 0; i < side; i++) for (let j = 0; j < side; j++) {
    const cls = cover[Math.min(size - 1, Math.floor(i / mesh.gridRes * size)) * size + Math.min(size - 1, Math.floor(j / mesh.gridRes * size))];
    mask[i * side + j] = cls === LANDCOVER.urban || cls === LANDCOVER.garden || cls === LANDCOVER.drive || cls === LANDCOVER.parking || cls === LANDCOVER.paving || cls === LANDCOVER.concrete ? 1 : 0;
  }
  if (!mask.some(v => v > 0)) return undefined;
  const influence = smoothOn(mesh, mask);
  for (let v = 0; v < influence.length; v++) {
    const urban = Math.min(1, influence[v] / 0.35);
    influence[v] = urban * urban * (3 - 2 * urban);
  }
  return influence;
}

/**
 * Keep broad hills, but let neighbouring streets and houses share gentle
 * ground. `sigma`, how wide it is smoothed: on the coarse models their bumps
 * go (`CITY_SMOOTH`); on the laser ground model only the small ones between
 * the blocks (`LASER_CITY_SMOOTH`), each moved at most `cap` metres
 * (`LASER_CITY_CAP`), its slopes and banks kept as they stand.
 */
export function cityGround(mesh: TerrainMeshData, pristine: Float32Array, cover: Uint8Array, size: number,
  urban = urbanWeight(mesh, cover, size), sigma = CITY_SMOOTH, cap = Infinity): Float32Array {
  if (urban === undefined) return pristine;
  const ground = smoothOn(mesh, pristine, sigma);
  // Eased in from the tile's edge over what the smoothing reaches past it,
  // so tiles meet where neither knows the other's ground.
  const ramp = sigma * 96 / CITY_SMOOTH;
  const out = Float32Array.from(pristine);
  for (let i = 1; i < mesh.gridRes; i++) for (let j = 1; j < mesh.gridRes; j++) {
    const v = i * mesh.gridRes + i + j;
    const edge = Math.min(j * mesh.width / mesh.gridRes, (mesh.gridRes - j) * mesh.width / mesh.gridRes,
      i * mesh.depth / mesh.gridRes, (mesh.gridRes - i) * mesh.depth / mesh.gridRes);
    const t = Math.min(1, edge / ramp);
    // Fully simplify built-up blocks, with a soft transition at their outskirts.
    // No small cut/fill cap: tall DEM lumps should not survive as city mounds.
    const change = Math.max(-cap, Math.min(cap, ground[v] - pristine[v]));
    out[v] += change * urban[v] * t * t * (3 - 2 * t);
  }
  return out;
}

/** Clip a water triangle to a terrain cell, retaining interpolated water heights. */
type Point = [number, number, number];
function clip(poly: Point[], axis: 0 | 2, bound: number, sign: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const da = (a[axis] - bound) * sign, db = (b[axis] - bound) * sign;
    if (da >= 0) out.push(a);
    if ((da < 0) !== (db < 0)) {
      const t = da / (da - db);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
    }
  }
  return out;
}

/**
 * Lower cells intersecting mapped water, including narrow canals between grid
 * vertices. The polygon stays at its exact map outline; holes stay dry except
 * for the unavoidable one-cell bank transition. Shared tile edges stay fixed.
 */
export function carveWater(mesh: TerrainMeshData, positions: Float32Array, indices: Uint32Array): boolean {
  const { gridRes: res, gridHeights: heights } = mesh;
  const side = res + 1, dx = mesh.width / res, dz = mesh.depth / res;
  let changed = false;
  for (let t = 0; t < indices.length; t += 3) {
    const tri: Point[] = [0, 1, 2].map(k => {
      const v = indices[t + k] * 3;
      return [positions[v], positions[v + 1], positions[v + 2]];
    });
    const x0 = Math.max(0, Math.floor(Math.min(...tri.map(p => p[0])) / dx));
    const x1 = Math.min(res - 1, Math.floor(Math.max(...tri.map(p => p[0])) / dx));
    const z0 = Math.max(0, Math.floor(Math.min(...tri.map(p => p[2])) / dz) - 1);
    const z1 = Math.min(res - 1, Math.floor(Math.max(...tri.map(p => p[2])) / dz) + 1);
    for (let i = z0; i <= z1; i++) for (let j = x0; j <= x1; j++) {
      // Use the mesh's actual Mercator row positions, not an assumed regular Z grid.
      const north = mesh.positions[(i * side) * 3 + 2], south = mesh.positions[((i + 1) * side) * 3 + 2];
      let poly = clip(clip(tri, 0, j * dx, 1), 0, (j + 1) * dx, -1);
      poly = clip(clip(poly, 2, north, 1), 2, south, -1);
      if (poly.length < 3) continue;
      const bed = Math.min(...poly.map(p => p[1])) - 0.65;
      for (const [row, col] of [[i, j], [i, j + 1], [i + 1, j], [i + 1, j + 1]]) {
        if (row === 0 || col === 0 || row === res || col === res) continue;
        const v = row * side + col;
        if (heights[v] > bed) { heights[v] = bed; changed = true; }
      }
    }
  }
  return changed;
}

/** Channel depth under the banks for a waterway line of this half-width, metres: a ditch to a river. */
export function channelDepth(halfWidth: number): number {
  return Math.min(1.2, Math.max(0.3, 0.3 + halfWidth * 0.25));
}

/** Most a channel is cut under the ground beside it, past its own depth, metres. */
const CHANNEL_CUT = 2.5;
/** How near the tile's edge a waterway's end is taken to be on it, metres. */
const EDGE_EPS = 0.05;
/** The width a channel's cut is full past its edge and eases out over, metres: about a grid cell. */
const CHANNEL_CELL = 6.8;
/** How far either side of a waterway its channel looks for the bottom of its valley, metres past its edge. */
const VALLEY_REACH = 12;

/**
 * Cut a channel along every mapped waterway line: a stream or a small river
 * mapped only as a line lies in the ground, under its banks, not draped on
 * the field. The elevation model's valley rarely lies where the map draws
 * the stream: draped, it ran along the valley's side, half a metre over the
 * lower bank. So each stretch is cut to the lowest ground across it, out to
 * `VALLEY_REACH` past its edges, less the channel's depth, within a cell of
 * the water's edge, easing out over the next cell.
 *
 * Toward the tile's edge the cut eases instead to the channel's depth under
 * the ground where it is. That depends only on the line and the vertex's
 * place, never on this tile's ground beyond it, so a vertex on the edge is
 * lowered just as the neighbouring tile lowers it, and the two still meet.
 * On the edge itself only the distance from where a line crosses it counts:
 * the map cuts a waterway at the tile's edge, and each tile has only its
 * own half of it.
 * `positions` are the ribbons' flat x, z pairs, a left and a right edge per
 * cross-section.
 */
export function carveWaterways(mesh: TerrainMeshData, positions: Float32Array, indices: Uint32Array): boolean {
  const { gridRes: res, gridHeights: heights } = mesh;
  // A fixed width, not this tile's own cell: tiles in different rows differ
  // in size, and the cut eased out differently on either side of the edge.
  const side = res + 1, cell = CHANNEL_CELL;
  const lower = new Float32Array(heights.length);
  const quads = Math.floor(indices.length / 6);
  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6], b = a + 2;
    const ax = (positions[a * 2] + positions[(a + 1) * 2]) / 2, az = (positions[a * 2 + 1] + positions[(a + 1) * 2 + 1]) / 2;
    const bx = (positions[b * 2] + positions[(b + 1) * 2]) / 2, bz = (positions[b * 2 + 1] + positions[(b + 1) * 2 + 1]) / 2;
    const hw = Math.hypot(positions[a * 2] - positions[(a + 1) * 2], positions[a * 2 + 1] - positions[(a + 1) * 2 + 1]) / 2;
    const depth = channelDepth(hw);
    const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez, len = Math.sqrt(len2);
    // The bottom of the valley across this stretch, at both its ends and its middle.
    let bed = Infinity;
    if (len > 1e-6) {
      const nx = -ez / len, nz = ex / len, across = hw + VALLEY_REACH;
      for (const t of [0, 0.5, 1]) {
        for (let o = -across; o <= across + 1e-6; o += 3) {
          const x = ax + ex * t + nx * o, z = az + ez * t + nz * o;
          if (x < 0 || z < 0 || x > mesh.width || z > mesh.depth) continue;
          bed = Math.min(bed, sampleGrid(mesh, x, z));
        }
      }
    }
    bed -= depth;
    const full = hw + cell * 0.6, reach = full + cell;
    const j0 = Math.max(0, Math.floor((Math.min(ax, bx) - reach) / mesh.width * res));
    const j1 = Math.min(res, Math.ceil((Math.max(ax, bx) + reach) / mesh.width * res));
    const i0 = Math.max(0, Math.floor((Math.min(az, bz) - reach) / mesh.depth * res) - 1);
    const i1 = Math.min(res, Math.ceil((Math.max(az, bz) + reach) / mesh.depth * res) + 1);
    // Where this stretch meets the tile's edge, by edge: north, south, west,
    // east. The map cuts a waterway there, and the neighbour's half of it
    // begins at the same point.
    const crossings: Array<[number, number, number]> = [];
    for (const [edge, axis, at] of [[0, 1, 0], [1, 1, mesh.depth], [2, 0, 0], [3, 0, mesh.width]] as const) {
      const pa = axis === 0 ? ax : az, pb = axis === 0 ? bx : bz;
      if (Math.min(pa, pb) > at + EDGE_EPS || Math.max(pa, pb) < at - EDGE_EPS) continue;
      const u = Math.abs(pb - pa) < 1e-9 ? 0 : Math.max(0, Math.min(1, (at - pa) / (pb - pa)));
      crossings.push([edge, ax + ex * u, az + ez * u]);
    }
    for (let i = i0; i <= i1; i++) for (let j = j0; j <= j1; j++) {
      const v = i * side + j;
      const x = mesh.positions[v * 3], z = mesh.positions[v * 3 + 2];
      let d: number;
      if (i === 0 || j === 0 || i === res || j === res) {
        // On the edge, only from where a line crosses it, on the same edge:
        // the map cuts a waterway at the tile's edge, with nothing of the
        // neighbour's half, so each tile cut the edge by its own half
        // only, and by any stream near it on its own side: 30 cm apart.
        d = Infinity;
        for (const [edge, cx, cz] of crossings) {
          const same = (edge === 0 && i === 0) || (edge === 1 && i === res) || (edge === 2 && j === 0) || (edge === 3 && j === res);
          if (same) d = Math.min(d, Math.hypot(x - cx, z - cz));
        }
      } else {
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2)) : 0;
        d = Math.hypot(x - ax - ex * t, z - az - ez * t);
      }
      if (d >= reach) continue;
      const w0 = d <= full ? 1 : 1 - (d - full) / (reach - full);
      const w = w0 * w0 * (3 - 2 * w0);
      const relative = depth * w;
      const toBed = Number.isFinite(bed) ? Math.min(depth + CHANNEL_CUT, Math.max(relative, heights[v] - bed)) * w : relative;
      // Within two cells of the tile's edge, eased back to the relative cut.
      const edge = Math.min(Math.min(i, j, res - i, res - j) / 2, 1);
      lower[v] = Math.max(lower[v], relative + (toBed - relative) * edge);
    }
  }
  let changed = false;
  for (let v = 0; v < heights.length; v++) {
    if (lower[v] <= 0) continue;
    heights[v] -= lower[v];
    changed = true;
  }
  return changed;
}

/** Bilinear height of the grid at chunk-local (x, z), on its own triangulation's cell. */
function sampleGrid(mesh: TerrainMeshData, x: number, z: number): number {
  const res = mesh.gridRes, side = res + 1;
  const fx = Math.max(0, Math.min(res - 1e-6, x / mesh.width * res)), fz = Math.max(0, Math.min(res - 1e-6, z / mesh.depth * res));
  const j = Math.floor(fx), i = Math.floor(fz), u = fx - j, w = fz - i;
  const h = mesh.gridHeights;
  return (h[i * side + j] * (1 - u) + h[i * side + j + 1] * u) * (1 - w) + (h[(i + 1) * side + j] * (1 - u) + h[(i + 1) * side + j + 1] * u) * w;
}
